import { dedupeTabsById } from '../../../utils/layoutTabUtils';
import type {
  Tab,
  TabGroup,
  TabNavigationState,
  TargetTabWorkspace,
} from '../../layoutStore';
import { createTargetTabWorkspaceFromTabs } from '../../layoutTargetWorkspace';
import { isPersistedRecord } from '../persist-migration';
import { migrateLegacyTargetScopeKey } from '../target-scope-key.migration';

function isPersistedTab(value: unknown): value is Tab {
  return (
    isPersistedRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.type === 'string'
  );
}

function isPersistedTabGroup(value: unknown): value is TabGroup {
  return (
    isPersistedRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.type === 'string' &&
    Array.isArray(value.tabIds) &&
    value.tabIds.every((tabId) => typeof tabId === 'string')
  );
}

function isPersistedTabNavigationState(
  value: unknown,
): value is TabNavigationState {
  return (
    isPersistedRecord(value) &&
    Array.isArray(value.entries) &&
    typeof value.currentIndex === 'number'
  );
}

function normalizeTargetTabWorkspace(
  value: unknown,
): TargetTabWorkspace | undefined {
  if (Array.isArray(value)) {
    const validTabs = value.filter(isPersistedTab);

    if (validTabs.length === 0) {
      return undefined;
    }

    return createTargetTabWorkspaceFromTabs({ tabs: validTabs });
  }

  if (!isPersistedRecord(value) || !Array.isArray(value.tabs)) {
    return undefined;
  }

  const tabs = dedupeTabsById({ tabs: value.tabs.filter(isPersistedTab) });

  if (tabs.length === 0) {
    return undefined;
  }

  const tabIds = new Set(tabs.map((tab) => tab.id));
  const tabNavigationByTabId: Record<string, TabNavigationState> =
    isPersistedRecord(value.tabNavigationByTabId)
      ? Object.entries(value.tabNavigationByTabId).reduce<
          Record<string, TabNavigationState>
        >((accumulator, [tabId, navigation]) => {
          if (tabIds.has(tabId) && isPersistedTabNavigationState(navigation)) {
            accumulator[tabId] = navigation;
          }

          return accumulator;
        }, {})
      : {};

  const tabHistory = Array.isArray(value.tabHistory)
    ? value.tabHistory.filter(
        (tabId): tabId is string =>
          typeof tabId === 'string' && tabIds.has(tabId),
      )
    : [];

  const tabGroups = Array.isArray(value.tabGroups)
    ? value.tabGroups
        .filter(isPersistedTabGroup)
        .map((group) => ({
          ...group,
          tabIds: group.tabIds.filter((tabId) => tabIds.has(tabId)),
        }))
        .filter((group) => group.tabIds.length > 0)
    : [];

  const tabOrder = Array.isArray(value.tabOrder)
    ? value.tabOrder.filter(
        (tabId): tabId is string => typeof tabId === 'string',
      )
    : tabs.map((tab) => tab.id);

  const activeMainContentTabId =
    typeof value.activeMainContentTabId === 'string' &&
    tabIds.has(value.activeMainContentTabId)
      ? value.activeMainContentTabId
      : undefined;

  return {
    tabs: tabs.map((tab) => ({ ...tab, isActive: false })),
    tabNavigationByTabId,
    tabHistory,
    activeMainContentTabId,
    tabGroups,
    tabOrder,
  };
}

function mergeTargetTabWorkspaces({
  left,
  right,
}: {
  left: TargetTabWorkspace;
  right: TargetTabWorkspace;
}): TargetTabWorkspace {
  const tabs = dedupeTabsById({ tabs: [...left.tabs, ...right.tabs] });
  const tabIds = new Set(tabs.map((tab) => tab.id));
  const seenOrder = new Set<string>();
  const tabOrder = [...left.tabOrder, ...right.tabOrder].filter((tabId) => {
    if (seenOrder.has(tabId)) {
      return false;
    }

    seenOrder.add(tabId);

    return true;
  });

  return {
    tabs,
    tabNavigationByTabId: {
      ...left.tabNavigationByTabId,
      ...right.tabNavigationByTabId,
    },
    tabHistory: [...left.tabHistory, ...right.tabHistory].filter(
      (tabId, index, all) => tabIds.has(tabId) && all.indexOf(tabId) === index,
    ),
    activeMainContentTabId:
      right.activeMainContentTabId ?? left.activeMainContentTabId,
    tabGroups: [...left.tabGroups, ...right.tabGroups].filter(
      (group, index, all) =>
        all.findIndex((candidate) => candidate.id === group.id) === index,
    ),
    tabOrder: tabOrder.length > 0 ? tabOrder : tabs.map((tab) => tab.id),
  };
}

export function migrateSavedPinnedTabsKeys(
  savedPinnedTabs: unknown,
): Record<string, TargetTabWorkspace> {
  if (!isPersistedRecord(savedPinnedTabs)) {
    return {};
  }

  const merged: Record<string, TargetTabWorkspace> = {};

  for (const [key, value] of Object.entries(savedPinnedTabs)) {
    const workspace = normalizeTargetTabWorkspace(value);

    if (!workspace) continue;

    const nextKey = migrateLegacyTargetScopeKey(key);

    merged[nextKey] = merged[nextKey]
      ? mergeTargetTabWorkspaces({ left: merged[nextKey], right: workspace })
      : workspace;
  }

  return merged;
}
